import { DataTable } from "@/components/molecules/tables/data-table";
import { Button } from "@/components/ui/button";
import { useConnectors } from "@/features/connectors/hooks/use-connectors";
import { useNumberRequests } from "@/features/number-requests/hooks/use-number-requests";
import { useTranslation } from "@/features/utils/i18n";
import { PaintBrushIcon } from "@phosphor-icons/react";
import { Link, useParams } from "react-router-dom";
import { PageLayout } from "../layout";
import { useConnectorsBulkActions } from "./components/bulk-actions";
import { useConnectorColumns } from "./components/columns";
import { AddChannelDialog } from "./components/add-channel";
import { useNumberRequestColumns } from "./components/number-requests/columns";
import { CreateNumberRequestDialog } from "./components/number-requests/create-dialog";
import { TemporaryConnectorCallout } from "./components/temporary-callout";

export function ConnectorsPage() {
  const { t } = useTranslation("connectors");
  const columns = useConnectorColumns();
  const { t: tRequests } = useTranslation("numberRequests");
  const { company: clientId } = useParams<{ company: string }>();
  const { data: connectors = [], isLoading } = useConnectors(clientId || "");
  const bulkActions = useConnectorsBulkActions();

  // Number requests live here rather than on their own page: a request *is* a
  // channel, one that doesn't exist yet. Splitting them meant the client had to
  // know which of two pages their phone line was on depending on whether it had
  // been provisioned.
  const { data: requests = [], isLoading: isLoadingRequests } =
    useNumberRequests(clientId || "");
  const requestColumns = useNumberRequestColumns();

  // At most one per tenant (enforced server-side), and it is the one channel
  // whose row cannot explain itself — hence the callout above the table.
  const temporary = connectors.find(
    (connector) => connector.type === "temporary",
  );

  const pendingRequests = requests.filter(
    (request) =>
      request.state === "draft" || request.state === "documents_pending",
  );

  return (
    <PageLayout
      title={t("connectors.title")}
      description={t("connectors.description")}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to={`/${clientId}/settings/connectors/personalization`}>
              <PaintBrushIcon className="h-4 w-4" />
              {t("connectors.personalization", "Personnalisation")}
            </Link>
          </Button>
          <AddChannelDialog />
        </div>
      }
    >
      {temporary && <TemporaryConnectorCallout connector={temporary} />}

      <DataTable
        columns={columns}
        data={connectors}
        isLoading={isLoading}
        hideSearch={true}
        bulkActions={bulkActions}
      />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold tracking-tight">
              {tRequests("numberRequests.title")}
              {!!pendingRequests.length && (
                <div className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-medium text-white">
                  {pendingRequests.length}
                </div>
              )}
            </h3>
            <p className="text-muted-foreground text-sm">
              {tRequests("numberRequests.description")}
            </p>
          </div>
          <CreateNumberRequestDialog />
        </div>

        <DataTable
          columns={requestColumns}
          data={requests}
          isLoading={isLoadingRequests}
          hideSearch
        />
      </div>
    </PageLayout>
  );
}
